import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import { Button } from '@/components/ui/button'
import { Toast } from '@/components/ui/toast'
import {
  ArrowLeft,
  Trophy,
  Users,
  Calendar,
  Clock,
  CheckCircle,
  XCircle,
  Package,
  Star,
  TrendingUp,
  Award,
  Medal,
  Crown,
  Target,
  Flag,
  Plus,
  Edit,
  Trash2,
  User
} from 'lucide-react'

interface Competition {
  id: string
  title: string
  description: string
  start_date: string
  end_date: string
  status: 'upcoming' | 'active' | 'completed' | 'cancelled'
  prize: string
  created_at: string
  created_by: string
  participants: {
    manager_id: string
    manager_name: string
    team_name: string
    team_color: string
    score: number
    completed_missions: number
    team_members: number
  }[]
  winner_id?: string
}

export function AdminCompetitionsPage() {
  const navigate = useNavigate()
  const [competitions, setCompetitions] = useState<Competition[]>([])
  const [loading, setLoading] = useState(true)
  const [showToast, setShowToast] = useState(false)
  const [toastMessage, setToastMessage] = useState('')
  const [toastType, setToastType] = useState<'success' | 'error'>('success')
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [selectedCompetition, setSelectedCompetition] = useState<Competition | null>(null)
  const [managers, setManagers] = useState<any[]>([])
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    start_date: '',
    end_date: '',
    prize: '',
    participants: [] as string[]
  })

  useEffect(() => {
    fetchCompetitions()
    fetchManagers()
  }, [])

  const fetchCompetitions = async () => {
    try {
      setLoading(true)
      const { data, error } = await supabase
        .from('competitions')
        .select('*')
        .order('created_at', { ascending: false })

      if (error) throw error

      // For each competition, fetch participants
      const competitionsWithParticipants = await Promise.all(
        (data || []).map(async (competition) => {
          const { data: participants, error: participantsError } = await supabase
            .from('competition_participants')
            .select(`
              manager_id,
              team_name,
              team_color,
              score,
              completed_missions,
              profiles:manager_id (
                full_name
              )
            `)
            .eq('competition_id', competition.id)

          if (participantsError) throw participantsError

          // For each participant, count team members
          const participantsWithTeamSize = await Promise.all(
            (participants || []).map(async (participant) => {
              const { count, error: countError } = await supabase
                .from('delivery_partners')
                .select('*', { count: 'exact' })
                .eq('manager_id', participant.manager_id)
                .eq('status', 'active')

              if (countError) throw countError

              return {
                manager_id: participant.manager_id,
                manager_name: participant.profiles?.full_name || 'Sans nom',
                team_name: participant.team_name,
                team_color: participant.team_color,
                score: participant.score,
                completed_missions: participant.completed_missions,
                team_members: count || 0
              }
            })
          )

          return {
            ...competition,
            participants: participantsWithTeamSize
          }
        })
      )

      setCompetitions(competitionsWithParticipants)
    } catch (err) {
      console.error('Error fetching competitions:', err)
      setToastMessage('Erreur lors du chargement des compétitions')
      setToastType('error')
      setShowToast(true)
    } finally {
      setLoading(false)
    }
  }

  const fetchManagers = async () => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select(`
          id,
          full_name,
          email,
          region,
          delivery_partners!delivery_partners_manager_id_fkey (
            id,
            status
          )
        `)
        .eq('role', 'manager')
        .eq('status', 'active')

      if (error) throw error

      // Filter managers with at least one active delivery partner
      const managersWithTeams = data?.filter(manager => 
        manager.delivery_partners?.some(partner => partner.status === 'active')
      ) || []

      setManagers(managersWithTeams)
    } catch (err) {
      console.error('Error fetching managers:', err)
    }
  }

  const handleCreateCompetition = async () => {
    try {
      if (!formData.title || !formData.start_date || !formData.end_date || formData.participants.length < 2) {
        setToastMessage('Veuillez remplir tous les champs obligatoires et sélectionner au moins 2 équipes')
        setToastType('error')
        setShowToast(true)
        return
      }

      const startDate = new Date(formData.start_date)
      const endDate = new Date(formData.end_date)
      
      if (startDate >= endDate) {
        setToastMessage('La date de fin doit être postérieure à la date de début')
        setToastType('error')
        setShowToast(true)
        return
      }

      // Get current user
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) throw new Error('User not authenticated')

      // Create competition
      const { data: competition, error: competitionError } = await supabase
        .from('competitions')
        .insert([{
          title: formData.title,
          description: formData.description,
          start_date: formData.start_date,
          end_date: formData.end_date,
          status: new Date() >= startDate ? 'active' : 'upcoming',
          prize: formData.prize,
          created_by: user.id
        }])
        .select()
        .single()

      if (competitionError) throw competitionError

      // Create participants
      const participants = formData.participants.map(managerId => {
        const manager = managers.find(m => m.id === managerId)
        return {
          competition_id: competition.id,
          manager_id: managerId,
          team_name: `Équipe ${manager?.full_name || 'Sans nom'}`,
          team_color: getRandomColor(),
          score: 0,
          completed_missions: 0
        }
      })

      const { error: participantsError } = await supabase
        .from('competition_participants')
        .insert(participants)

      if (participantsError) throw participantsError

      // Create notifications for managers
      const notifications = formData.participants.map(managerId => ({
        user_id: managerId,
        title: 'Nouvelle compétition',
        content: `Vous avez été inscrit à la compétition "${formData.title}"`,
        type: 'system',
        metadata: {
          competition_id: competition.id
        }
      }))

      await supabase
        .from('notifications')
        .insert(notifications)

      setToastMessage('Compétition créée avec succès')
      setToastType('success')
      setShowToast(true)
      setShowCreateModal(false)
      resetForm()
      fetchCompetitions()
    } catch (err) {
      console.error('Error creating competition:', err)
      setToastMessage('Erreur lors de la création de la compétition')
      setToastType('error')
      setShowToast(true)
    }
  }

  const handleUpdateCompetition = async () => {
    if (!selectedCompetition) return

    try {
      if (!formData.title || !formData.start_date || !formData.end_date) {
        setToastMessage('Veuillez remplir tous les champs obligatoires')
        setToastType('error')
        setShowToast(true)
        return
      }

      const startDate = new Date(formData.start_date)
      const endDate = new Date(formData.end_date)
      
      if (startDate >= endDate) {
        setToastMessage('La date de fin doit être postérieure à la date de début')
        setToastType('error')
        setShowToast(true)
        return
      }

      // Update competition
      const { error: updateError } = await supabase
        .from('competitions')
        .update({
          title: formData.title,
          description: formData.description,
          start_date: formData.start_date,
          end_date: formData.end_date,
          status: new Date() >= startDate ? 'active' : 'upcoming',
          prize: formData.prize
        })
        .eq('id', selectedCompetition.id)

      if (updateError) throw updateError

      setToastMessage('Compétition mise à jour avec succès')
      setToastType('success')
      setShowToast(true)
      setShowEditModal(false)
      fetchCompetitions()
    } catch (err) {
      console.error('Error updating competition:', err)
      setToastMessage('Erreur lors de la mise à jour de la compétition')
      setToastType('error')
      setShowToast(true)
    }
  }

  const handleDeleteCompetition = async () => {
    if (!selectedCompetition) return

    try {
      // Delete competition participants
      const { error: participantsError } = await supabase
        .from('competition_participants')
        .delete()
        .eq('competition_id', selectedCompetition.id)

      if (participantsError) throw participantsError

      // Delete competition
      const { error: competitionError } = await supabase
        .from('competitions')
        .delete()
        .eq('id', selectedCompetition.id)

      if (competitionError) throw competitionError

      setToastMessage('Compétition supprimée avec succès')
      setToastType('success')
      setShowToast(true)
      setShowDeleteModal(false)
      fetchCompetitions()
    } catch (err) {
      console.error('Error deleting competition:', err)
      setToastMessage('Erreur lors de la suppression de la compétition')
      setToastType('error')
      setShowToast(true)
    }
  }

  const handleCompleteCompetition = async (competitionId: string) => {
    try {
      // Get competition participants
      const { data: participants, error: participantsError } = await supabase
        .from('competition_participants')
        .select('*')
        .eq('competition_id', competitionId)
        .order('score', { ascending: false })

      if (participantsError) throw participantsError

      if (!participants || participants.length === 0) {
        setToastMessage('Aucun participant trouvé pour cette compétition')
        setToastType('error')
        setShowToast(true)
        return
      }

      // Determine winner
      const winner = participants[0]

      // Update competition status and winner
      const { error: updateError } = await supabase
        .from('competitions')
        .update({
          status: 'completed',
          winner_id: winner.manager_id
        })
        .eq('id', competitionId)

      if (updateError) throw updateError

      // Create notifications for all participants
      const notifications = participants.map(participant => ({
        user_id: participant.manager_id,
        title: 'Compétition terminée',
        content: participant.manager_id === winner.manager_id
          ? 'Félicitations ! Votre équipe a remporté la compétition !'
          : 'La compétition est terminée. Merci pour votre participation !',
        type: 'system',
        metadata: {
          competition_id: competitionId,
          is_winner: participant.manager_id === winner.manager_id
        }
      }))

      await supabase
        .from('notifications')
        .insert(notifications)

      setToastMessage('Compétition terminée avec succès')
      setToastType('success')
      setShowToast(true)
      fetchCompetitions()
    } catch (err) {
      console.error('Error completing competition:', err)
      setToastMessage('Erreur lors de la finalisation de la compétition')
      setToastType('error')
      setShowToast(true)
    }
  }

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      start_date: '',
      end_date: '',
      prize: '',
      participants: []
    })
  }

  const getRandomColor = () => {
    const colors = [
      '#EF4444', // Red
      '#F59E0B', // Amber
      '#10B981', // Emerald
      '#3B82F6', // Blue
      '#8B5CF6', // Violet
      '#EC4899', // Pink
      '#06B6D4', // Cyan
      '#F97316', // Orange
      '#84CC16', // Lime
      '#6366F1'  // Indigo
    ]
    return colors[Math.floor(Math.random() * colors.length)]
  }

  const getStatusBadge = (status: Competition['status']) => {
    switch (status) {
      case 'upcoming':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
            <Clock className="h-3 w-3 mr-1" />
            À venir
          </span>
        )
      case 'active':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            <CheckCircle className="h-3 w-3 mr-1" />
            En cours
          </span>
        )
      case 'completed':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
            <Trophy className="h-3 w-3 mr-1" />
            Terminée
          </span>
        )
      case 'cancelled':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
            <XCircle className="h-3 w-3 mr-1" />
            Annulée
          </span>
        )
      default:
        return null
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      {showToast && (
        <Toast
          type={toastType}
          title={toastMessage}
          onClose={() => setShowToast(false)}
        />
      )}

      {/* Create Competition Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full m-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-start mb-6">
              <h3 className="text-lg font-medium text-gray-900">
                Créer une nouvelle compétition
              </h3>
              <button
                onClick={() => setShowCreateModal(false)}
                className="text-gray-400 hover:text-gray-500"
              >
                <XCircle className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-6">
              <div>
                <label htmlFor="title" className="block text-sm font-medium text-gray-700">
                  Titre *
                </label>
                <input
                  type="text"
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                  required
                />
              </div>

              <div>
                <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                  Description
                </label>
                <textarea
                  id="description"
                  rows={3}
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                />
              </div>

              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <div>
                  <label htmlFor="start_date" className="block text-sm font-medium text-gray-700">
                    Date de début *
                  </label>
                  <input
                    type="date"
                    id="start_date"
                    value={formData.start_date}
                    onChange={(e) => setFormData(prev => ({ ...prev, start_date: e.target.value }))}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="end_date" className="block text-sm font-medium text-gray-700">
                    Date de fin *
                  </label>
                  <input
                    type="date"
                    id="end_date"
                    value={formData.end_date}
                    onChange={(e) => setFormData(prev => ({ ...prev, end_date: e.target.value }))}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                    required
                  />
                </div>
              </div>

              <div>
                <label htmlFor="prize" className="block text-sm font-medium text-gray-700">
                  Prix / Récompense
                </label>
                <input
                  type="text"
                  id="prize"
                  value={formData.prize}
                  onChange={(e) => setFormData(prev => ({ ...prev, prize: e.target.value }))}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                  placeholder="Ex: Bonus de 100$, Trophée, etc."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Participants *
                </label>
                <div className="space-y-2 max-h-60 overflow-y-auto border border-gray-300 rounded-md p-2">
                  {managers.length === 0 ? (
                    <div className="text-center py-4">
                      <p className="text-sm text-gray-500">Aucun manager disponible</p>
                    </div>
                  ) : (
                    managers.map(manager => (
                      <div key={manager.id} className="flex items-center p-2 hover:bg-gray-50 rounded-md">
                        <input
                          type="checkbox"
                          id={`manager-${manager.id}`}
                          checked={formData.participants.includes(manager.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setFormData(prev => ({
                                ...prev,
                                participants: [...prev.participants, manager.id]
                              }))
                            } else {
                              setFormData(prev => ({
                                ...prev,
                                participants: prev.participants.filter(id => id !== manager.id)
                              }))
                            }
                          }}
                          className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                        />
                        <label htmlFor={`manager-${manager.id}`} className="ml-3 block text-sm text-gray-700">
                          <span className="font-medium">{manager.full_name || 'Sans nom'}</span>
                          <div className="text-xs text-gray-500">
                            <span className="mr-2">{manager.region || 'Région non spécifiée'}</span>
                            <span>{manager.delivery_partners?.filter((p: any) => p.status === 'active').length || 0} livreurs actifs</span>
                          </div>
                        </label>
                      </div>
                    ))
                  )}
                </div>
                {formData.participants.length > 0 && (
                  <p className="mt-2 text-sm text-gray-500">
                    {formData.participants.length} équipe(s) sélectionnée(s)
                  </p>
                )}
              </div>
            </div>

            <div className="mt-6 flex justify-end space-x-3">
              <Button
                variant="secondary"
                onClick={() => setShowCreateModal(false)}
              >
                Annuler
              </Button>
              <Button
                variant="primary"
                onClick={handleCreateCompetition}
              >
                Créer la compétition
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Competition Modal */}
      {showEditModal && selectedCompetition && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full m-4">
            <div className="flex justify-between items-start mb-6">
              <h3 className="text-lg font-medium text-gray-900">
                Modifier la compétition
              </h3>
              <button
                onClick={() => setShowEditModal(false)}
                className="text-gray-400 hover:text-gray-500"
              >
                <XCircle className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-6">
              <div>
                <label htmlFor="edit-title" className="block text-sm font-medium text-gray-700">
                  Titre *
                </label>
                <input
                  type="text"
                  id="edit-title"
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                  required
                />
              </div>

              <div>
                <label htmlFor="edit-description" className="block text-sm font-medium text-gray-700">
                  Description
                </label>
                <textarea
                  id="edit-description"
                  rows={3}
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                />
              </div>

              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <div>
                  <label htmlFor="edit-start_date" className="block text-sm font-medium text-gray-700">
                    Date de début *
                  </label>
                  <input
                    type="date"
                    id="edit-start_date"
                    value={formData.start_date}
                    onChange={(e) => setFormData(prev => ({ ...prev, start_date: e.target.value }))}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="edit-end_date" className="block text-sm font-medium text-gray-700">
                    Date de fin *
                  </label>
                  <input
                    type="date"
                    id="edit-end_date"
                    value={formData.end_date}
                    onChange={(e) => setFormData(prev => ({ ...prev, end_date: e.target.value }))}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                    required
                  />
                </div>
              </div>

              <div>
                <label htmlFor="edit-prize" className="block text-sm font-medium text-gray-700">
                  Prix / Récompense
                </label>
                <input
                  type="text"
                  id="edit-prize"
                  value={formData.prize}
                  onChange={(e) => setFormData(prev => ({ ...prev, prize: e.target.value }))}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                  placeholder="Ex: Bonus de 100$, Trophée, etc."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Participants
                </label>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-500">
                    Les participants ne peuvent pas être modifiés après la création de la compétition.
                  </p>
                  <div className="mt-2 space-y-2">
                    {selectedCompetition.participants.map(participant => (
                      <div key={participant.manager_id} className="flex items-center">
                        <div 
                          className="h-4 w-4 rounded-full mr-2" 
                          style={{ backgroundColor: participant.team_color }}
                        ></div>
                        <span className="text-sm font-medium">{participant.team_name}</span>
                        <span className="text-xs text-gray-500 ml-2">
                          ({participant.team_members} livreurs)
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-6 flex justify-end space-x-3">
              <Button
                variant="secondary"
                onClick={() => setShowEditModal(false)}
              >
                Annuler
              </Button>
              <Button
                variant="primary"
                onClick={handleUpdateCompetition}
              >
                Mettre à jour
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && selectedCompetition && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full m-4">
            <div className="flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mx-auto">
              <XCircle className="h-6 w-6 text-red-600" />
            </div>

            <div className="mt-3 text-center sm:mt-5">
              <h3 className="text-lg font-medium text-gray-900">
                Confirmer la suppression
              </h3>
              <div className="mt-2">
                <p className="text-sm text-gray-500">
                  Êtes-vous sûr de vouloir supprimer la compétition "{selectedCompetition.title}" ? Cette action est irréversible.
                </p>
              </div>
            </div>

            <div className="mt-5 sm:mt-6 flex justify-end space-x-3">
              <Button
                variant="secondary"
                onClick={() => setShowDeleteModal(false)}
              >
                Annuler
              </Button>
              <Button
                variant="outline"
                className="text-red-600 hover:text-red-700"
                onClick={handleDeleteCompetition}
              >
                Supprimer
              </Button>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <div className="flex items-center">
            <Button
              variant="ghost"
              onClick={() => navigate('/admin')}
              className="mr-4"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                Compétitions
              </h1>
              <p className="mt-1 text-sm text-gray-500">
                Créez et gérez des compétitions entre les équipes de livraison
              </p>
            </div>
          </div>
          <Button
            variant="primary"
            onClick={() => {
              resetForm()
              setShowCreateModal(true)
            }}
          >
            <Plus className="h-5 w-5 mr-2" />
            Nouvelle Compétition
          </Button>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : competitions.length === 0 ? (
          <div className="bg-white shadow rounded-lg">
            <div className="text-center py-12">
              <Trophy className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">Aucune compétition</h3>
              <p className="mt-1 text-sm text-gray-500">
                Commencez par créer une compétition entre les équipes de livraison
              </p>
              <Button
                variant="primary"
                className="mt-4"
                onClick={() => {
                  resetForm()
                  setShowCreateModal(true)
                }}
              >
                <Plus className="h-5 w-5 mr-2" />
                Créer une compétition
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-8">
            {/* Active Competitions */}
            <div className="bg-white shadow rounded-lg overflow-hidden">
              <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
                <h2 className="text-lg font-medium text-gray-900">
                  Compétitions en cours
                </h2>
              </div>
              <div className="p-6">
                <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                  {competitions
                    .filter(comp => comp.status === 'active')
                    .map(competition => (
                      <div 
                        key={competition.id}
                        className="bg-white border-2 border-gray-200 rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow"
                      >
                        <div className="p-5 border-b border-gray-200 bg-gradient-to-r from-blue-500 to-purple-600">
                          <div className="flex justify-between items-start">
                            <Trophy className="h-8 w-8 text-white" />
                            {getStatusBadge(competition.status)}
                          </div>
                          <h3 className="mt-2 text-xl font-bold text-white">{competition.title}</h3>
                          <div className="mt-2 flex items-center text-white/80">
                            <Calendar className="h-4 w-4 mr-1" />
                            <span className="text-sm">
                              {new Date(competition.start_date).toLocaleDateString()} - {new Date(competition.end_date).toLocaleDateString()}
                            </span>
                          </div>
                        </div>
                        
                        <div className="p-5">
                          {competition.description && (
                            <p className="text-sm text-gray-600 mb-4">{competition.description}</p>
                          )}
                          
                          {competition.prize && (
                            <div className="flex items-center mb-4 text-amber-600">
                              <Award className="h-5 w-5 mr-2" />
                              <span className="text-sm font-medium">{competition.prize}</span>
                            </div>
                          )}
                          
                          <h4 className="text-sm font-medium text-gray-700 mb-2">Classement</h4>
                          <div className="space-y-2 mb-4">
                            {[...competition.participants]
                              .sort((a, b) => b.score - a.score)
                              .map((participant, index) => (
                                <div 
                                  key={participant.manager_id}
                                  className="flex items-center justify-between p-2 rounded-lg"
                                  style={{ backgroundColor: `${participant.team_color}20` }}
                                >
                                  <div className="flex items-center">
                                    <div 
                                      className="h-6 w-6 rounded-full flex items-center justify-center text-white text-xs font-bold"
                                      style={{ backgroundColor: participant.team_color }}
                                    >
                                      {index + 1}
                                    </div>
                                    <span className="ml-2 text-sm font-medium">{participant.team_name}</span>
                                  </div>
                                  <div className="text-sm font-bold">{participant.score} pts</div>
                                </div>
                              ))
                            }
                          </div>
                          
                          <div className="flex justify-between items-center">
                            <div className="text-xs text-gray-500">
                              {competition.participants.length} équipe(s)
                            </div>
                            <div className="flex space-x-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  setSelectedCompetition(competition)
                                  setFormData({
                                    title: competition.title,
                                    description: competition.description || '',
                                    start_date: competition.start_date,
                                    end_date: competition.end_date,
                                    prize: competition.prize || '',
                                    participants: []
                                  })
                                  setShowEditModal(true)
                                }}
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                className="text-red-600"
                                onClick={() => {
                                  setSelectedCompetition(competition)
                                  setShowDeleteModal(true)
                                }}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="primary"
                                size="sm"
                                onClick={() => handleCompleteCompetition(competition.id)}
                              >
                                <Flag className="h-4 w-4 mr-1" />
                                Terminer
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  
                  {competitions.filter(comp => comp.status === 'active').length === 0 && (
                    <div className="col-span-full text-center py-8">
                      <p className="text-sm text-gray-500">Aucune compétition en cours</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Upcoming Competitions */}
            <div className="bg-white shadow rounded-lg overflow-hidden">
              <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
                <h2 className="text-lg font-medium text-gray-900">
                  Compétitions à venir
                </h2>
              </div>
              <div className="p-6">
                <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                  {competitions
                    .filter(comp => comp.status === 'upcoming')
                    .map(competition => (
                      <div 
                        key={competition.id}
                        className="bg-white border-2 border-gray-200 rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow"
                      >
                        <div className="p-5 border-b border-gray-200 bg-gradient-to-r from-gray-500 to-gray-600">
                          <div className="flex justify-between items-start">
                            <Trophy className="h-8 w-8 text-white" />
                            {getStatusBadge(competition.status)}
                          </div>
                          <h3 className="mt-2 text-xl font-bold text-white">{competition.title}</h3>
                          <div className="mt-2 flex items-center text-white/80">
                            <Calendar className="h-4 w-4 mr-1" />
                            <span className="text-sm">
                              {new Date(competition.start_date).toLocaleDateString()} - {new Date(competition.end_date).toLocaleDateString()}
                            </span>
                          </div>
                        </div>
                        
                        <div className="p-5">
                          {competition.description && (
                            <p className="text-sm text-gray-600 mb-4">{competition.description}</p>
                          )}
                          
                          {competition.prize && (
                            <div className="flex items-center mb-4 text-amber-600">
                              <Award className="h-5 w-5 mr-2" />
                              <span className="text-sm font-medium">{competition.prize}</span>
                            </div>
                          )}
                          
                          <h4 className="text-sm font-medium text-gray-700 mb-2">Participants</h4>
                          <div className="space-y-2 mb-4">
                            {competition.participants.map((participant) => (
                              <div 
                                key={participant.manager_id}
                                className="flex items-center p-2 rounded-lg"
                                style={{ backgroundColor: `${participant.team_color}20` }}
                              >
                                <div 
                                  className="h-4 w-4 rounded-full mr-2"
                                  style={{ backgroundColor: participant.team_color }}
                                ></div>
                                <span className="text-sm">{participant.team_name}</span>
                                <span className="text-xs text-gray-500 ml-2">
                                  ({participant.team_members} livreurs)
                                </span>
                              </div>
                            ))}
                          </div>
                          
                          <div className="flex justify-between items-center">
                            <div className="text-xs text-gray-500">
                              {competition.participants.length} équipe(s)
                            </div>
                            <div className="flex space-x-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  setSelectedCompetition(competition)
                                  setFormData({
                                    title: competition.title,
                                    description: competition.description || '',
                                    start_date: competition.start_date,
                                    end_date: competition.end_date,
                                    prize: competition.prize || '',
                                    participants: []
                                  })
                                  setShowEditModal(true)
                                }}
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                className="text-red-600"
                                onClick={() => {
                                  setSelectedCompetition(competition)
                                  setShowDeleteModal(true)
                                }}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  
                  {competitions.filter(comp => comp.status === 'upcoming').length === 0 && (
                    <div className="col-span-full text-center py-8">
                      <p className="text-sm text-gray-500">Aucune compétition à venir</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Completed Competitions */}
            <div className="bg-white shadow rounded-lg overflow-hidden">
              <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
                <h2 className="text-lg font-medium text-gray-900">
                  Compétitions terminées
                </h2>
              </div>
              <div className="p-6">
                <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                  {competitions
                    .filter(comp => comp.status === 'completed')
                    .map(competition => {
                      const winner = competition.participants.find(p => p.manager_id === competition.winner_id);
                      
                      return (
                        <div 
                          key={competition.id}
                          className="bg-white border-2 border-gray-200 rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow"
                        >
                          <div className="p-5 border-b border-gray-200 bg-gradient-to-r from-amber-500 to-amber-600">
                            <div className="flex justify-between items-start">
                              <Trophy className="h-8 w-8 text-white" />
                              {getStatusBadge(competition.status)}
                            </div>
                            <h3 className="mt-2 text-xl font-bold text-white">{competition.title}</h3>
                            <div className="mt-2 flex items-center text-white/80">
                              <Calendar className="h-4 w-4 mr-1" />
                              <span className="text-sm">
                                {new Date(competition.start_date).toLocaleDateString()} - {new Date(competition.end_date).toLocaleDateString()}
                              </span>
                            </div>
                          </div>
                          
                          <div className="p-5">
                            {competition.description && (
                              <p className="text-sm text-gray-600 mb-4">{competition.description}</p>
                            )}
                            
                            {winner && (
                              <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                                <div className="flex items-center">
                                  <Crown className="h-6 w-6 text-amber-500 mr-2" />
                                  <div>
                                    <h4 className="text-sm font-medium text-gray-900">Gagnant</h4>
                                    <p className="text-sm text-amber-700">{winner.team_name}</p>
                                  </div>
                                </div>
                                <div className="mt-2 flex items-center text-amber-700">
                                  <Star className="h-4 w-4 mr-1" />
                                  <span className="text-sm">{winner.score} points</span>
                                </div>
                              </div>
                            )}
                            
                            {competition.prize && (
                              <div className="flex items-center mb-4 text-amber-600">
                                <Award className="h-5 w-5 mr-2" />
                                <span className="text-sm font-medium">{competition.prize}</span>
                              </div>
                            )}
                            
                            <h4 className="text-sm font-medium text-gray-700 mb-2">Résultats finaux</h4>
                            <div className="space-y-2 mb-4">
                              {[...competition.participants]
                                .sort((a, b) => b.score - a.score)
                                .map((participant, index) => (
                                  <div 
                                    key={participant.manager_id}
                                    className="flex items-center justify-between p-2 rounded-lg"
                                    style={{ backgroundColor: `${participant.team_color}20` }}
                                  >
                                    <div className="flex items-center">
                                      <div 
                                        className="h-6 w-6 rounded-full flex items-center justify-center text-white text-xs font-bold"
                                        style={{ backgroundColor: participant.team_color }}
                                      >
                                        {index + 1}
                                      </div>
                                      <span className="ml-2 text-sm font-medium">{participant.team_name}</span>
                                    </div>
                                    <div className="text-sm font-bold">{participant.score} pts</div>
                                  </div>
                                ))
                              }
                            </div>
                            
                            <div className="text-xs text-gray-500">
                              Terminée le {new Date(competition.end_date).toLocaleDateString()}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  
                  {competitions.filter(comp => comp.status === 'completed').length === 0 && (
                    <div className="col-span-full text-center py-8">
                      <p className="text-sm text-gray-500">Aucune compétition terminée</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}